import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';

import { selectClient } from '@client/model';
import { useFeatureFlags } from '@client/contexts/FeatureFlagsContext';
import { clientsApi, clientKeys } from '@client/api/waterlily';
import { guidedPlanningApi, guidedPlanningSessionKeys } from '@/features/guided-financial-planning/api';

interface PersistedSelection {
  action: string;
  partnerFundingSourceId?: string;
  originalValue: number;
  customClientPercent?: number;
}

/**
 * Fetches the partner client and their active guided planning session, then
 * returns the set of funding source IDs that the partner shared TO the current
 * client. These should be rendered as read-only throughout the guided flow.
 */
export function usePartnerLockedFundingSources() {
  const client = useSelector(selectClient);
  const { flags } = useFeatureFlags();
  const featureEnabled = flags.partnerFundingSplit === true;

  const hasPartnerClientId = featureEnabled && !!client?.partnerClientId;

  const { data: partnerClient = null, isLoading: isLoadingPartnerClient } = useQuery({
    queryKey: clientKeys.detail(client?.partnerClientId ?? ''),
    queryFn: async () => {
      const response = (await clientsApi.getById(
        client!.partnerClientId!,
      )) as unknown as ClientContainer;
      return response.client ?? null;
    },
    enabled: hasPartnerClientId,
    staleTime: 5 * 60 * 1000,
  });

  const { data: partnerSessions, isLoading: isLoadingPartnerSessions } = useQuery({
    queryKey: guidedPlanningSessionKeys.list({ clientId: partnerClient?.clientId ?? '' }),
    queryFn: () => guidedPlanningApi.getSessions(partnerClient!.clientId),
    enabled: !!partnerClient?.clientId,
    staleTime: 5 * 60 * 1000,
  });

  const isLoading = hasPartnerClientId && (isLoadingPartnerClient || isLoadingPartnerSessions);

  const partnerActiveSession = partnerSessions?.[0] ?? null;

  const {
    partnerSharedFundingSourceIds,
    partnerSharedActionByFundingSourceId,
    partnerSharedOriginalValueByFundingSourceId,
    partnerSharedCustomPercentByFundingSourceId,
  } = useMemo(() => {
    const ids = new Set<string>();
    const actionMap: Record<string, string> = {};
    const originalValueMap: Record<string, number> = {};
    const customPercentMap: Record<string, number | undefined> = {};

    // Primary: derive from the current client's own sources via addedByClientId.
    // Works for splits initiated by either the modal or guided planning, and is
    // independent of any session metadata.
    const currentClientId = client?.clientId ?? null;
    const mySelfFunding = client?.multipleFundingSources?.selfFunding ?? {};
    Object.entries(mySelfFunding).forEach(([id, source]) => {
      if (source.partnerAction !== 'mirrored') return;
      if (!source.addedByClientId) return;
      if (currentClientId != null && source.addedByClientId === currentClientId) return;
      ids.add(id);
      const splitRatio = source.splitRatio ?? null;
      actionMap[id] =
        splitRatio == null ? 'mirror' : splitRatio === 50 ? 'split-50-50' : 'split-custom';
      originalValueMap[id] = source.existingAssetsValue ?? 0;
      customPercentMap[id] =
        splitRatio != null && splitRatio !== 50 ? splitRatio : undefined;
    });

    // Fallback: legacy splits without `addedByClientId` — read partner's session
    // metadata (only populated when the partner used guided planning).
    const selections = partnerActiveSession?.stepData?.partnerAllocationSelections ?? {};
    const entries = Object.values(selections as Record<string, PersistedSelection>);
    for (const s of entries) {
      if (!s.partnerFundingSourceId || ids.has(s.partnerFundingSourceId)) continue;
      ids.add(s.partnerFundingSourceId);
      actionMap[s.partnerFundingSourceId] = s.action;
      originalValueMap[s.partnerFundingSourceId] = s.originalValue;
      customPercentMap[s.partnerFundingSourceId] = s.customClientPercent;
    }

    return {
      partnerSharedFundingSourceIds: ids,
      partnerSharedActionByFundingSourceId: actionMap,
      partnerSharedOriginalValueByFundingSourceId: originalValueMap,
      partnerSharedCustomPercentByFundingSourceId: customPercentMap,
    };
  }, [
    client?.clientId,
    client?.multipleFundingSources?.selfFunding,
    partnerActiveSession?.stepData?.partnerAllocationSelections,
  ]);

  return {
    isLoading,
    partnerClient,
    partnerActiveSession,
    partnerSharedFundingSourceIds,
    partnerSharedActionByFundingSourceId,
    partnerSharedOriginalValueByFundingSourceId,
    partnerSharedCustomPercentByFundingSourceId,
    partnerFirstName: partnerClient?.clientFirstName ?? null,
  };
}
